import { Navigate } from "react-router-dom";
import { useAuthSession } from "src/hooks/useAuthSession";
import { useDeepLink } from "src/hooks/useDeepLink";
import { AuthenticatedShell } from "src/routing/components/AuthenticatedShell";
import { RouteEnvironmentProviders } from "src/routing/components/RouteEnvironment";
import { signInRoute } from "src/routing/domains/authPaths";

/** Auth gate for logged-in domain trees.
 *
 * Role: data router root `Component` on each domain `RouteObject` in `src/routing/domains/*.ts`.
 * Renders `<Outlet/>` for domain chrome + pages. Does not define URLs itself.
 *
 * Guest-only URLs (`/signIn`, `/auth/gcallback`) use `GuestShell` instead. PDF URLs use `PdfLayout`.
 * Unknown URLs use `NotFoundShell` (logged-in chrome when authenticated; bare NotFound when not).
 *
 * Tests/stories that need the post-login provider stack without Cognito should use
 * `appShell: true` on `render` / `setStory` (see `AppShellTestUtils.tsx`) — never substitute
 * this component with an ungated wrapper in production route trees.
 */
export function AppShell() {
  const { isAuthenticating, isAuthenticated } = useAuthSession();
  useDeepLink(isAuthenticated, isAuthenticating);

  if (isAuthenticating) {
    return <div />;
  }

  if (!isAuthenticated) {
    return (
      <RouteEnvironmentProviders>
        <Navigate to={signInRoute.path} replace />
      </RouteEnvironmentProviders>
    );
  }

  return <AuthenticatedShell />;
}
